import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, MailOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { deleteOutboundDraft, listOutboundDrafts, listOutboundHistory } from "../../../api/v3/mailHubApi";
import { formatMailHubError, readMailHubData } from "../lib/contracts";
import "./OutboxListPage.css";

const { Text, Title } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const STATUS_META = {
    completed: { label: "已完成", color: "success" },
    pending: { label: "待发送", color: "default" },
    sending: { label: "发送中", color: "processing" },
    failed: { label: "发送失败", color: "error" },
};
const SEND_MODE_META = {
    single: { label: "标准发送", color: "blue" },
    batch_independent: { label: "群发模式", color: "purple" },
};

function requireRecord(value, context) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(`${context} 必须是对象`);
    }
    return value;
}

function requireString(value, context) {
    if (typeof value !== "string" || value.length === 0) {
        throw new Error(`${context} 必须是非空字符串`);
    }
    return value;
}

function requireNullableString(value, context) {
    if (value === null || typeof value === "string") {
        return value;
    }
    throw new Error(`${context} 必须是字符串或 null`);
}

function requireDateString(value, context) {
    if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
        throw new Error(`${context} 必须是合法时间字符串`);
    }
    return value;
}

function requireNullableDateString(value, context) {
    if (value === null || (typeof value === "string" && !Number.isNaN(Date.parse(value)))) {
        return value;
    }
    throw new Error(`${context} 必须是合法时间字符串或 null`);
}

function requireNonNegativeInteger(value, context) {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`${context} 必须是非负整数`);
    }
    return value;
}

function requirePositiveInteger(value, context) {
    if (!Number.isInteger(value) || value < 1) {
        throw new Error(`${context} 必须是正整数`);
    }
    return value;
}

function validateRecipientList(value, context) {
    if (!Array.isArray(value)) {
        throw new Error(`${context} 必须是数组`);
    }
    return value.map((item, index) => {
        const record = requireRecord(item, `${context}[${index}]`);
        return requireString(record.address, `${context}[${index}].address`);
    });
}

function validateRecipientStatusCounts(value, context) {
    const record = requireRecord(value, context);
    return {
        total: requireNonNegativeInteger(record.total, `${context}.total`),
        dispatched: requireNonNegativeInteger(record.dispatched, `${context}.dispatched`),
        failed: requireNonNegativeInteger(record.failed, `${context}.failed`),
        bounced: requireNonNegativeInteger(record.bounced, `${context}.bounced`),
        complained: requireNonNegativeInteger(record.complained, `${context}.complained`),
        unknown: requireNonNegativeInteger(record.unknown, `${context}.unknown`),
    };
}

function validateOutboundHistoryPayload(payload) {
    const data = requireRecord(payload, "发件箱历史 data");
    if (!Array.isArray(data.items)) {
        throw new Error("发件箱历史.items 必须是数组");
    }
    const items = data.items.map((item, index) => {
        const record = requireRecord(item, `发件箱历史.items[${index}]`);
        const status = requireString(record.overall_status, `发件箱历史.items[${index}].overall_status`);
        if (!STATUS_META[status]) {
            throw new Error(`发件箱历史.items[${index}].overall_status 不受支持: ${status}`);
        }
        const sendMode = requireString(record.send_mode, `发件箱历史.items[${index}].send_mode`);
        if (!SEND_MODE_META[sendMode]) {
            throw new Error(`发件箱历史.items[${index}].send_mode 不受支持: ${sendMode}`);
        }
        return {
            receipt_id: requireString(record.receipt_id, `发件箱历史.items[${index}].receipt_id`),
            outbound_id: requireString(record.outbound_id, `发件箱历史.items[${index}].outbound_id`),
            canonical_message_id: requireString(record.canonical_message_id, `发件箱历史.items[${index}].canonical_message_id`),
            mailbox_id: requireString(record.mailbox_id, `发件箱历史.items[${index}].mailbox_id`),
            from_address: requireString(record.from_address, `发件箱历史.items[${index}].from_address`),
            to_addresses: validateRecipientList(record.to_addresses, `发件箱历史.items[${index}].to_addresses`),
            subject: requireNullableString(record.subject, `发件箱历史.items[${index}].subject`),
            send_mode: sendMode,
            overall_status: status,
            task_count: requireNonNegativeInteger(record.task_count, `发件箱历史.items[${index}].task_count`),
            recipient_status_counts: validateRecipientStatusCounts(record.recipient_status_counts, `发件箱历史.items[${index}].recipient_status_counts`),
            submitted_at: requireDateString(record.submitted_at, `发件箱历史.items[${index}].submitted_at`),
            completed_at: requireNullableDateString(record.completed_at, `发件箱历史.items[${index}].completed_at`),
        };
    });

    const pagination = requireRecord(data.pagination, "发件箱历史.pagination");
    return {
        items,
        pagination: {
            page: requirePositiveInteger(pagination.page, "发件箱历史.pagination.page"),
            pageSize: requirePositiveInteger(pagination.pageSize, "发件箱历史.pagination.pageSize"),
            total: requireNonNegativeInteger(pagination.total, "发件箱历史.pagination.total"),
        },
    };
}

function validateDraftPayload(payload) {
    const data = requireRecord(payload, "草稿列表 data");
    if (!Array.isArray(data.items)) {
        throw new Error("草稿列表.items 必须是数组");
    }
    const items = data.items.map((item, index) => {
        const record = requireRecord(item, `草稿列表.items[${index}]`);
        return {
            id: requireString(record.id, `草稿列表.items[${index}].id`),
            mailbox_id: requireNullableString(record.mailbox_id, `草稿列表.items[${index}].mailbox_id`),
            to_addresses: validateRecipientList(record.to_addresses, `草稿列表.items[${index}].to_addresses`),
            subject: requireNullableString(record.subject, `草稿列表.items[${index}].subject`),
            version: requirePositiveInteger(record.version, `草稿列表.items[${index}].version`),
            updated_at: requireDateString(record.updated_at, `草稿列表.items[${index}].updated_at`),
            last_autosaved_at: requireNullableDateString(record.last_autosaved_at, `草稿列表.items[${index}].last_autosaved_at`),
        };
    });

    const pagination = requireRecord(data.pagination, "草稿列表.pagination");
    return {
        items,
        pagination: {
            page: requirePositiveInteger(pagination.page, "草稿列表.pagination.page"),
            pageSize: requirePositiveInteger(pagination.pageSize, "草稿列表.pagination.pageSize"),
            total: requireNonNegativeInteger(pagination.total, "草稿列表.pagination.total"),
        },
    };
}

function renderRecipients(addresses) {
    const visible = addresses.slice(0, 2);
    const overflow = addresses.length - visible.length;
    return (
        <Space size={[4, 4]} wrap>
            {visible.map((address) => (
                <Tag key={address}>{address}</Tag>
            ))}
            {overflow > 0 ? <Tag color="blue">+{overflow}</Tag> : null}
        </Space>
    );
}

function buildRecipientStatusSummary(status, counts) {
    const total = counts.total;
    if (total === 0) {
        return STATUS_META[status].label;
    }

    const finished = Math.min(counts.dispatched + counts.failed + counts.bounced + counts.complained, total);
    if (status === "pending") {
        return `等待投递 ${total} 位收件人`;
    }
    if (status === "sending") {
        return `发送中 ${finished}/${total}`;
    }
    if (status === "failed" && counts.failed === total) {
        return "发送失败";
    }
    if (counts.dispatched === total) {
        return `已发送给全部 ${total} 位收件人`;
    }

    const parts = [`已发送 ${counts.dispatched}/${total}`];
    if (counts.bounced > 0) {
        parts.push(`${counts.bounced} 位退信`);
    }
    if (counts.complained > 0) {
        parts.push(`${counts.complained} 位投诉`);
    }
    if (counts.failed > 0) {
        parts.push(`${counts.failed} 位失败`);
    }
    if (counts.unknown > 0 && status !== "pending") {
        parts.push(`${counts.unknown} 位待确认`);
    }
    return parts.join("，");
}

function renderSendMode(sendMode) {
    const meta = SEND_MODE_META[sendMode];
    return <Tag color={meta.color}>{meta.label}</Tag>;
}

function renderStatus(status, record) {
    const meta = STATUS_META[status];
    const summary = buildRecipientStatusSummary(status, record.recipient_status_counts);
    return (
        <div className="mail-hub-outbox-status">
            <Tag color={meta.color}>{meta.label}</Tag>
            <Text type="secondary">{summary}</Text>
        </div>
    );
}

function renderSubmittedAt(value) {
    return (
        <div className="mail-hub-outbox-time">
            <span>{dayjs(value).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
    );
}

const columns = [
    {
        title: "发件邮箱",
        dataIndex: "from_address",
        key: "from_address",
        width: 240,
        ellipsis: true,
    },
    {
        title: "收件人",
        dataIndex: "to_addresses",
        key: "to_addresses",
        width: 280,
        render: renderRecipients,
    },
    {
        title: "主题",
        dataIndex: "subject",
        key: "subject",
        ellipsis: true,
        render: (value) => (value ? <Tooltip title={value}>{value}</Tooltip> : <Text type="secondary">(无主题)</Text>),
    },
    {
        title: "发送模式",
        dataIndex: "send_mode",
        key: "send_mode",
        width: 116,
        render: renderSendMode,
    },
    {
        title: "状态",
        dataIndex: "overall_status",
        key: "overall_status",
        width: 280,
        render: renderStatus,
    },
    {
        title: "提交时间",
        dataIndex: "submitted_at",
        key: "submitted_at",
        width: 190,
        render: renderSubmittedAt,
    },
];

const OutboxListPage = () => {
    const [loading, setLoading] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [draftItems, setDraftItems] = useState([]);
    const [loadError, setLoadError] = useState("");
    const [draftError, setDraftError] = useState("");
    const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
    const [draftPagination, setDraftPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });

    const loadHistory = useCallback(async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
        setLoading(true);
        setLoadError("");
        try {
            const response = await listOutboundHistory({ page, page_size: pageSize });
            const data = validateOutboundHistoryPayload(readMailHubData(response, "发件箱历史"));
            setItems(data.items);
            setPagination(data.pagination);
        } catch (error) {
            setItems([]);
            setPagination({ page, pageSize, total: 0 });
            setLoadError(formatMailHubError(error, "加载发件箱失败"));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDrafts = useCallback(async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
        setDraftLoading(true);
        setDraftError("");
        try {
            const response = await listOutboundDrafts({ page, page_size: pageSize });
            const data = validateDraftPayload(readMailHubData(response, "草稿列表"));
            setDraftItems(data.items);
            setDraftPagination(data.pagination);
        } catch (error) {
            setDraftItems([]);
            setDraftPagination({ page, pageSize, total: 0 });
            setDraftError(formatMailHubError(error, "加载草稿列表失败"));
        } finally {
            setDraftLoading(false);
        }
    }, []);

    const handleDeleteDraft = useCallback(async (draftId) => {
        setDraftLoading(true);
        try {
            await deleteOutboundDraft(draftId);
            await loadDrafts(draftPagination.page, draftPagination.pageSize);
        } catch (error) {
            setDraftError(formatMailHubError(error, "删除草稿失败"));
        } finally {
            setDraftLoading(false);
        }
    }, [draftPagination.page, draftPagination.pageSize, loadDrafts]);

    const draftColumns = [
        {
            title: "收件人",
            dataIndex: "to_addresses",
            key: "to_addresses",
            width: 280,
            render: renderRecipients,
        },
        {
            title: "主题",
            dataIndex: "subject",
            key: "subject",
            ellipsis: true,
            render: (value) => (value ? <Tooltip title={value}>{value}</Tooltip> : <Text type="secondary">(无主题)</Text>),
        },
        {
            title: "版本",
            dataIndex: "version",
            key: "version",
            width: 90,
            render: (value) => <Tag color="blue">v{value}</Tag>,
        },
        {
            title: "最近保存",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 190,
            render: renderSubmittedAt,
        },
        {
            title: "操作",
            key: "actions",
            width: 180,
            render: (_, record) => (
                <Space>

                    <Popconfirm
                        title="删除草稿"
                        description="草稿会被软删除，无法从列表继续打开。"
                        okText="删除"
                        cancelText="取消"
                        onConfirm={() => handleDeleteDraft(record.id)}
                    >
                        <Button size="small" danger type="link" icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        loadHistory(1, DEFAULT_PAGE_SIZE);
        loadDrafts(1, DEFAULT_PAGE_SIZE);
    }, [loadDrafts, loadHistory]);

    return (
        <section className="mail-hub-outbox-page">
            <header className="mail-hub-outbox-hero">
                <div>
                    <div className="mail-hub-outbox-kicker">
                        <MailOutlined />
                        <span>Mail Hub</span>
                    </div>
                    <Title level={2} className="mail-hub-outbox-title">发件箱</Title>
                    <Text className="mail-hub-outbox-subtitle">查看当前组织和权限范围内已提交的出站邮件。</Text>
                </div>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            loadDrafts(draftPagination.page, draftPagination.pageSize);
                            loadHistory(pagination.page, pagination.pageSize);
                        }}
                        loading={loading || draftLoading}
                    >
                        刷新
                    </Button>

                </Space>
            </header>

            {loadError ? (
                <Alert className="mail-hub-outbox-alert" type="error" showIcon message="发件箱不可用" description={loadError} />
            ) : null}

            {draftError ? (
                <Alert className="mail-hub-outbox-alert" type="error" showIcon message="草稿列表不可用" description={draftError} />
            ) : null}

            <div className="mail-hub-outbox-panel">
                <div className="mail-hub-outbox-status">
                    <Tag color="purple">草稿</Tag>
                    <Text type="secondary">保存后可从这里继续编辑内容块邮件。</Text>
                </div>
                <Table
                    rowKey="id"
                    loading={draftLoading}
                    columns={draftColumns}
                    dataSource={draftItems}
                    scroll={{ x: 920 }}
                    pagination={{
                        current: draftPagination.page,
                        pageSize: draftPagination.pageSize,
                        total: draftPagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                        showTotal: (total) => `共 ${total} 封草稿`,
                        onChange: loadDrafts,
                    }}
                />
            </div>

            <div className="mail-hub-outbox-panel">
                <Table
                    rowKey="outbound_id"
                    loading={loading}
                    columns={columns}
                    dataSource={items}
                    scroll={{ x: 1200 }}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                        showTotal: (total) => `共 ${total} 封出站邮件`,
                        onChange: loadHistory,
                    }}
                />
            </div>
        </section>
    );
};

export default OutboxListPage;
